import {
  CANCELED_PAYMENT_PERIOD,
  NON_PROFIT_PAYMENT_PLAN_ID,
  ONE_MONTH_PAYMENT_PERIOD,
  USE_BALANCE_PAYMENT_PERIOD,
} from './constants';
import type { PaymentPlanResponse } from '../../repositories';

export class PaymentPlan {
  constructor(
    private readonly isStripe: boolean,
    private readonly isSuspended: boolean,
    private apiResponse: PaymentPlanResponse,
    private readonly isTrialer = false
  ) {}

  public getApiResponse() {
    return this.apiResponse;
  }

  public getbaasPTGID() {
    return this.apiResponse.baasPTGID;
  }

  public getCurrentPaymentPeriod() {
    return this.apiResponse.currentPaymentPeriod;
  }

  public getDefaultCustomerPaymentPeriod() {
    const { currentPaymentPeriod } = this.apiResponse;

    // Trialer status comes from current plan type. If a trialer's payment-plan response says
    // currentPaymentPeriod = 1, keep selecting the monthly option instead of converting it to
    // the customer-only USE_BALANCE_PAYMENT_PERIOD (0) option that getPlanDiscounts excludes.
    if (this.isTrialer && !this.isSuspended) {
      return currentPaymentPeriod === CANCELED_PAYMENT_PERIOD ? ONE_MONTH_PAYMENT_PERIOD : currentPaymentPeriod;
    }

    // for stripe customers, we want to return the current payment period
    if (this.isStripe && !this.isSuspended) {
      return currentPaymentPeriod;
    }

    // canceled customers will have currentPaymentPeriod set to 0
    // we want to preselect 1 month plan in that case
    if (currentPaymentPeriod === CANCELED_PAYMENT_PERIOD) {
      return ONE_MONTH_PAYMENT_PERIOD;
    }

    // if customer is on 1 month plan, we want to preselect USE_BALANCE_PAYMENT_PERIOD (0)
    if (currentPaymentPeriod === ONE_MONTH_PAYMENT_PERIOD) {
      return USE_BALANCE_PAYMENT_PERIOD;
    }

    return currentPaymentPeriod;
  }

  public getDescription() {
    return this.apiResponse.description;
  }

  public getExpirationDate() {
    return this.apiResponse.expirationDate;
  }

  public getPaymentPlanGroupId() {
    return this.apiResponse.paymentPlanGroupId;
  }

  public getPlanDiscounts() {
    // For customers, selecting "Continue my current payment plan" prepay will be set to 0
    // We want to make sure that in that case we show discount which matches the current payment period

    const currentPaymentPeriodDiscount = this.apiResponse.planDiscounts.find(
      (discount) => discount.discountPeriod === this.apiResponse.currentPaymentPeriod
    );

    // for customers, return discounts array from API + addional discount for prepay USE_BALANCE_PAYMENT_PERIOD (0)
    if (currentPaymentPeriodDiscount && (this.isSuspended || (!this.isStripe && !this.isTrialer))) {
      return this.getBsv3CustomerDiscounts(currentPaymentPeriodDiscount);
    }

    // for trialers and stripe customers, return plain discounts array from API
    return this.apiResponse.planDiscounts;
  }

  private getBsv3CustomerDiscounts(
    currentPaymentPeriodDiscount: NonNullable<(typeof this.apiResponse.planDiscounts)[number]>
  ) {
    const duplicatedDiscount = {
      ...currentPaymentPeriodDiscount,
      id: USE_BALANCE_PAYMENT_PERIOD,
      discountPeriod: USE_BALANCE_PAYMENT_PERIOD,
    };

    const planDiscountsFromHighestToLowest = [...this.apiResponse.planDiscounts]
      .filter((discount) => discount.discountPeriod !== 1) // filter out 1 month discount
      .sort((a, b) => b.discountPeriod - a.discountPeriod);

    return [duplicatedDiscount, ...planDiscountsFromHighestToLowest];
  }

  /**
   * Returns available discounts for the change payment plan flow, sorted from highest to lowest.
   * For Stripe customers, excludes the current payment plan option since they cannot select
   * their current plan in the change payment plan flow.
   * @see https://ctct.atlassian.net/browse/CBU-19712
   */
  public getChangePaymentPlanDiscounts() {
    const sortedDiscounts = [...this.apiResponse.planDiscounts].sort((a, b) => b.discountPeriod - a.discountPeriod);

    if (this.isStripe) {
      const currentPeriod = this.getCurrentPaymentPeriod();

      return sortedDiscounts.filter((discount) => discount.discountPeriod !== currentPeriod);
    }

    return sortedDiscounts;
  }

  /**
   * Returns the preselected payment period for the change payment plan flow.
   * For Stripe customers, returns the first available period (since current plan is excluded).
   * For other customers, returns the current payment period.
   */
  public getPreselectedChangePaymentPlanPeriod() {
    if (this.isStripe) {
      const availableOptions = this.getChangePaymentPlanDiscounts();

      return availableOptions[0]?.discountPeriod ?? this.getCurrentPaymentPeriod();
    }

    return this.getCurrentPaymentPeriod();
  }

  public getDiscountPercentage(prepay: string) {
    const planDiscounts = this.getPlanDiscounts();
    const planDiscountByPrepay = planDiscounts.find(({ discountPeriod }) => discountPeriod.toString() === prepay);

    if (planDiscountByPrepay) {
      return planDiscountByPrepay.discountPercent;
    }

    return 0;
  }

  public getIsFree() {
    return this.apiResponse.free;
  }

  public getIsSelectable() {
    return this.apiResponse.selectable;
  }

  public getIsSticky() {
    return this.apiResponse.sticky;
  }

  public getIsNonProfit() {
    return this.apiResponse.paymentPlanGroupId === NON_PROFIT_PAYMENT_PLAN_ID;
  }

  public getIsStripe() {
    return this.isStripe;
  }
}
